<template lang="html">
  <NuxtPicture
    format="avif,webp"
    legacyFormat="png"
    :src="src"
    :provider="provider"
    :alt="props.alt"
    :sizes="props.sizes"
    :loading="props.loading"
    :img-attrs="imgAttrs"
    :class="base"
  />
</template>

<script setup lang="ts">
export type UiPictureVariant = 'imageFull' | 'native' | 'heightFull'

const props = withDefaults(
  defineProps<{
    src?: string
    fallback?: string
    provider?: 'ipx' | 'skubyApi'
    variant?: UiPictureVariant
    /** Альтернативный текст (доступность + картиночный поиск) */
    alt?: string
    /**
     * Реальная ширина слота (атрибут sizes): без него NuxtPicture считает
     * картинку вьюпортной, и браузер качает вариант 640px+ даже для
     * карточки в 300px
     */
    sizes?: string
    /**
     * Intrinsic width/height on <img> (CLS). Ratio matters more than px —
     * e.g. 1×1 reserves a square before the file loads. Does not change
     * the network variant (that stays driven by `sizes`).
     */
    width?: number
    height?: number
    /** 'lazy' для картинок ниже фолда — не конкурируют с LCP за сеть */
    loading?: 'lazy' | 'eager'
    /** 'high' только для LCP-кандидата (главный баннер, первый слайд PDP) */
    fetchpriority?: 'high' | 'low'
  }>(),
  {
    fallback: '/Plug.svg',
    provider: 'skubyApi',
    variant: 'native',
    alt: '',
  },
)

const src = computed(() => props.src || props.fallback)
const provider = computed(() => (props.src ? props.provider : 'ipx'))

// fetchpriority / intrinsic size — не пропы NuxtPicture, доносятся до <img>
const imgAttrs = computed(() => {
  const attrs: Record<string, string | number> = {}

  if (props.fetchpriority) attrs.fetchpriority = props.fetchpriority
  if (props.width != null) attrs.width = props.width
  if (props.height != null) attrs.height = props.height

  return Object.keys(attrs).length ? attrs : undefined
})

const base = computed(() => {
  const classes: string[] = []

  if (props.variant === 'imageFull')
    classes.push('[&>img]:w-full [&>img]:h-full [&>img]:object-cover')
  if (props.variant === 'heightFull')
    classes.push('[&>img]:h-full [&>img]:object-cover [&>img]:w-auto')

  return classes
})
</script>
